import {v4 as uuidv4} from 'uuid';
import {SpatialEntityData} from '../model/api';
import {BehaviorSubject, Observable} from 'rxjs';
import {share, shareReplay} from 'rxjs/operators';
import {Unsubscribable} from '@core/interfaces/unsubscribable';
import {GeospatialEntitiesConfiguration, GeospatialOutageEntitiesConfiguration} from '../model/viewer-config';
import {FormBuilder, FormGroup} from '@angular/forms';
import {GeospatialFilterStore} from '../api/geospatial-filter.store';
import {FilterAccordion} from '@theme/components/layout/control-panel/filter-accordion';
import {FilterControlsStore} from '../control-panel/components/filter/layer-filter.store';

export enum LayerType {
    SYSTEM = 'system',
    PROJECT = 'project',
    METRIC = 'metric',
    CUSTOM = 'custom',
    OUTAGE = 'outage',
}

export interface ILayer {
    /** Toggle layer visibility, which is available to users */
    toggleVisibility(): void;
    /** Update map data source, used as part of data refresh */
    updateMapDataSource(
        mapDataSource: GeospatialOutageEntitiesConfiguration | GeospatialOutageEntitiesConfiguration,
    ): void;
    /** Empty the data from the layer, but retain other details (e.g., visibility */
    clearMapDataSource(): void;
    /** Update layer name, displayed to user */
    updateName(newName: string): void;
    /** Update loading status of the layer */
    setLoading(value: boolean): void;
    /** Toggle layer expand/collapse status, in control panel? */
    toggleLayer(): void;
    /** Collapse layer controls, in control panel? */
    closeLayer(): void;
    /** Copies another layer into this instance */
    copy(layer: Layer): void;
    /** Listen for layer form changes */
    subscribeToFormChanges(): void;
    /** Initializes the form group and subscribes to filter changes */
    initForm(): void;
    /** Clears the layer filter state */
    clearFilters(): void;
}

export abstract class Layer extends Unsubscribable implements ILayer {
    /** map data array any type to be type cast by individual layer implementations */
    mapData: any[] = [];
    id: string;

    dataLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    dataLoading$: Observable<boolean> = this.dataLoading.asObservable().pipe(share(), shareReplay(1));

    refresh: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    refresh$: Observable<boolean> = this.refresh.asObservable().pipe(share());

    filterLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    filterLoading$: Observable<boolean> = this.filterLoading.asObservable().pipe(share(), shareReplay(1));

    filterControlsStore: FilterAccordion;

    constructor(
        public name: string,
        public type: LayerType,
        public fb: FormBuilder,
        public geospatialFilterStore: GeospatialFilterStore,
        public visible: boolean = true,
        public expanded: boolean = false,
    ) {
        super();

        this.filterControlsStore = new FilterControlsStore(this.fb, this.geospatialFilterStore);

        this.id = uuidv4(); // Generate a unique ID when a layer is created
    }

    toggleVisibility(): void {
        this.visible = !this.visible;
    }

    updateMapDataSource(mapDataSource: GeospatialEntitiesConfiguration | GeospatialOutageEntitiesConfiguration): void {}

    clearMapDataSource(): void {
        this.mapData = [];
    }

    updateName(newName: string): void {
        this.name = newName;
    }

    setLoading(value: boolean): void {
        this.dataLoading.next(value);
    }

    toggleLayer(): void {
        this.expanded = !this.expanded;
    }

    closeLayer(): void {
        this.expanded = false;
    }

    copy(layer: Layer) {
        this.name = layer.name;
        this.type = layer.type;
        this.visible = layer.visible;
        this.expanded = layer.expanded;
    }

    subscribeToFormChanges(): void {}

    initForm(): void {}

    clearFilters(): void {}

    /** determine if the layer has all valid settings to be displayed */
    layerCanBeDisplayed(): boolean {
        return this.visible;
    }
}
